import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Avatar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import productService from '../../../services/productService';
import { useStore } from '../../../context/StoreContext';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import ActiveFilter from '../../common/ActiveFilter';
import { buildToggleAction, buildSellOnlineAction, useCanUpdate } from '../../common/activeToggle';
import { useDataTable } from '../../../hooks/useDataTable';
import { useCurrency } from '../../../hooks/useCurrency';

const ProductsList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isSuperAdmin } = useStore();
  const canToggle = useCanUpdate('products.update');
  const { format } = useCurrency();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);

  // Page, size, search, sort and the active filter all live in the URL (/products?page=2&size=20&search=...).
  const table = useDataTable(productService.getProductsPage, {}, {
    onError: () => toast.error(t('products.fetchError')),
  });
  const fetchProducts = table.refresh;

  const handleDeleteClick = (product) => {
    setProductToDelete(product);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await productService.deleteProduct(productToDelete.id);
      toast.success(t('products.deleteSuccess'));
      setDeleteDialogOpen(false);
      setProductToDelete(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.deleteError'));
      console.error(error);
    }
  };

  const handleToggleSellOnline = async (row) => {
    try {
      await productService.toggleProductSellOnline(row.id);
      toast.success(row.sellOnline === false ? t('sellOnline.enabled') : t('sellOnline.disabled'));
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.toggleError'));
    }
  };

  const handleToggleActive = async (row) => {
    try {
      await productService.toggleProductActive(row.id);
      toast.success(row.isActive === false ? t('common.activated') : t('common.deactivated'));
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.toggleError'));
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setProductToDelete(null);
  };

  const priceRangeLabel = ({ lowestPrice, highestPrice, basePrice }) => {
    if (lowestPrice && highestPrice && lowestPrice !== highestPrice) {
      return `${format(lowestPrice)} - ${format(highestPrice)}`;
    }
    return format(lowestPrice || basePrice || 0);
  };

  // Only real DB columns can be sorted server-side (name, sku, basePrice, isActive); computed ones can't.
  const columns = [
    {
      field: 'mainImageUrl',
      headerName: t('products.mainImageUrl'),
      width: 80,
      sortable: false,
      cellRenderer: (params) => (
        <Avatar
          src={params.value}
          variant="rounded"
          sx={{ width: 40, height: 40, my: 0.5 }}
        >
          <ImageIcon />
        </Avatar>
      ),
    },
    { field: 'id', headerName: t('common.id'), width: 70, sortable: false },
    { field: 'name', headerName: t('common.name'), width: 250 },
    { field: 'sku', headerName: t('products.sku'), width: 120 },
    {
      field: 'basePrice',
      headerName: t('products.basePrice'),
      width: 120,
      cellRenderer: (params) => format(params.value ?? 0),
    },
    {
      field: 'priceRange',
      headerName: t('common.price'),
      width: 180,
      sortable: false,
      cellRenderer: (params) => priceRangeLabel(params.data),
      // 'priceRange' isn't a real field on the row (it's computed from lowestPrice/
      // highestPrice/basePrice), so the default value-based tooltip would show
      // nothing — mirror the cellRenderer's own logic here.
      tooltipValueGetter: (params) => priceRangeLabel(params.data),
    },
    {
      field: 'primaryCategory',
      headerName: t('categories.category'),
      width: 150,
      sortable: false,
      cellRenderer: (params) => params.value?.name || 'N/A',
      // primaryCategory is an object — the default tooltip skips objects to avoid
      // "[object Object]", so show its display name explicitly instead.
      tooltipValueGetter: (params) => params.value?.name || null,
    },
    {
      field: 'variantCount',
      headerName: t('products.variants'),
      width: 110,
      sortable: false,
      cellRenderer: (params) => (
        <Chip label={params.value || 0} size="small" color="primary" />
      ),
    },
    {
      field: 'totalStock',
      headerName: t('common.quantity'),
      width: 120,
      sortable: false,
      cellRenderer: (params) => {
        const stock = params.value || 0;
        const color = stock > 10 ? 'success' : stock > 0 ? 'warning' : 'error';
        return <Chip label={stock} size="small" color={color} />;
      },
    },
    ...(isSuperAdmin
      ? [
          {
            field: 'storeName',
            headerName: t('roles.store'),
            width: 150,
            sortable: false,
            cellRenderer: (params) => `${params.value} (${params.data.storeCode})`,
          },
        ]
      : []),
    {
      field: 'isActive',
      headerName: t('common.status'),
      width: 110,
      cellRenderer: (params) => (
        <Chip
          label={params.value ? t('common.active') : t('common.inactive')}
          color={params.value ? 'success' : 'default'}
          size="small"
        />
      ),
    },
    {
      field: 'sellOnline',
      headerName: t('sellOnline.column'),
      width: 120,
      sortable: false,
      cellRenderer: (params) => (
        <Chip
          label={params.value === false ? t('sellOnline.offline') : t('sellOnline.online')}
          color={params.value === false ? 'default' : 'info'}
          variant="outlined"
          size="small"
        />
      ),
      tooltipValueGetter: () => null,
    },
    {
      field: 'actions',
      headerName: t('common.actions'),
      width: 180,
      sortable: false,
      cellRenderer: (params) => (
        <GridActionsCell
          actions={[
            {
              key: 'view',
              icon: VisibilityIcon,
              title: t('common.details'),
              color: 'info',
              onClick: () => navigate(`/products/view/${params.data.id}`),
            },
            {
              key: 'edit',
              icon: EditIcon,
              title: t('common.edit'),
              color: 'primary',
              onClick: () => navigate(`/products/edit/${params.data.id}`),
            },
            buildToggleAction({ row: params.data, onToggle: handleToggleActive, t, canToggle }),
            buildSellOnlineAction({ row: params.data, onToggle: handleToggleSellOnline, t, canToggle }),
            {
              key: 'delete',
              icon: DeleteIcon,
              title: t('common.delete'),
              color: 'error',
              onClick: () => handleDeleteClick(params.data),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">{t('products.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/products/create')}
        >
          {t('products.createProduct')}
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableSearch
            placeholder={t('products.searchPlaceholder')}
            value={table.searchInput}
            onChange={table.handleSearchChange}
            sx={{ mb: 2 }}
          />

          <ActiveFilter
            value={table.params.active || 'all'}
            onChange={(active) => table.updateParams({ active })}
            sx={{ mb: 2 }}
          />

          <AppDataGrid
            rows={table.data.content}
            columns={columns}
            loading={table.loading}
            paginationMode="server"
            rowCount={table.data.totalElements}
            paginationModel={table.paginationModel}
            onPaginationModelChange={table.handlePaginationModelChange}
            sortModel={table.sortModel}
            onSortModelChange={table.handleSortChange}
            pageSizeOptions={[10, 20, 50, 100]}
            getRowId={(row) => row.id}
          />
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('products.deleteProduct')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('common.deleteConfirmText', { name: productToDelete?.name })}
            {productToDelete?.variantCount > 0 && (
              <>
                <br />
                <strong style={{ color: 'red' }}>
                  {t('products.variants')}: {productToDelete.variantCount}
                </strong>
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProductsList;
